import React, { useEffect, useRef, useState } from "react";

/**
 * SSR-safe wrapper around Google's <model-viewer> web component.
 *
 * The library registers a custom element and touches `window` / `customElements`
 * on import, so it must only run on the client. We also defer both the library
 * import and the (multi-MB) GLB fetch until the card nears the viewport, keeping
 * the homepage's initial payload light. Background is transparent so the host
 * container's own gradient shows through instead of a hard black box.
 */
interface ModelViewerProps {
  /** Path to a .glb/.gltf model (e.g. "/glb/marscompress.glb"). */
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
}

// model-viewer is a native custom element, not a React component — typed as `any`
// so TSX accepts its hyphenated attributes; at runtime this string renders as the
// intrinsic element.
const ModelViewerEl: any = "model-viewer";

export default function ModelViewer({ src, alt, className, style }: ModelViewerProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const [reduced, setReduced] = useState(false);

  // Lazy-load the viewer library + model only when the card nears the viewport.
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    setReduced(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    let alive = true;
    const load = () =>
      import("@google/model-viewer").then(() => {
        if (alive) setReady(true);
      });

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          load();
        }
      },
      { rootMargin: "300px" }
    );
    io.observe(el);
    return () => {
      alive = false;
      io.disconnect();
    };
  }, []);

  const fill: React.CSSProperties = { width: "100%", height: "100%", background: "transparent", ...style };

  // Placeholder keeps layout stable (and triggers the observer) until ready.
  if (!ready) {
    return <div ref={hostRef} className={className} style={fill} aria-label={alt} aria-busy="true" />;
  }

  return (
    <ModelViewerEl
      src={src}
      alt={alt}
      camera-controls=""
      disable-zoom=""
      auto-rotate={reduced ? undefined : ""}
      auto-rotate-delay="0"
      rotation-per-second="22deg"
      interaction-prompt="none"
      shadow-intensity="0"
      environment-image="neutral"
      exposure="1.05"
      loading="eager"
      className={`reg-model ${className ?? ""}`.trim()}
      style={fill}
    />
  );
}
